import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DietQualityBar from '../../components/DietQualityBar';
import SmartTooltipChip from '../../components/SmartTooltipChip';
import { getDietQualityColor } from '../../lib/themes/generalTheme';
import DietPatternImage from '../../components/DietPatternImage';
import useDiet from '../../lib/hooks/useDiet';
import { UserContext } from '../../lib/contexts';

const useStyles = makeStyles((theme) => ({
  frame: {
    ...theme.globalClasses.frame,
    marginTop: '0px',
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    padding: '24px',
    boxSizing: 'border-box',
  },
  title: {
    ...theme.globalClasses.title,
    color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
    fontSize: '18.72px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '116.7%',
    marginBottom: '14px',
  },
  subTitle: {
    ...theme.globalClasses.subTitle,
    color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dietBar: {
    width: '100%',
    maxWidth: '300px',
    marginBottom: '0px',
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  sectionMargin: {
    marginBottom: '24px',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: '24px',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  contentColumn: {
    flex: '0 1 66%',
  },
  imageColumn: {
    flex: '0 1 34%',
  },
  nestedGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    [theme.breakpoints.up('md')]: {
      marginTop: '24px',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  nestedColumn: {
    flex: '1 1 50%',
  },
  titleBarContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  mobileLayout: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  chipContainer: {
    marginBottom: '16px',
  },
}));

/**
 * DietBox component that displays a whole card or a variant which just shows the image and chip
 *
 * @param {Object} props - The component props
 * @param {string} props.title - The title of the diet box
 * @param {string} props.type - The type of the diet
 * @param {number} props.quality - The quality of the diet
 * @param {string} props.variant - The variant of the diet box
 * @param {string} props.chipPosition - Position of the chip relative to the image ('top' or 'bottom')
 * @returns {React.ReactElement | null} The rendered DietBox component
 */
const DietBox = ({ title, type, quality, variant = 'full', chipPosition = 'top' }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state: { user },
  } = React.useContext(UserContext);

  let { isLoading, data } = useDiet(type, quality, user?.id);
  data = data?.data || data;

  // Early return if loading or if both name and quality are null
  if (isLoading || (data?.name === null && data?.quality === null)) {
    return null;
  }

  const getChipColor = (value) => {
    return `${getDietQualityColor(value)}80`;
  };

  const ChipComponent = () => {
    if (!data?.name || !data?.quality) return null;
    return (
      <Box className={classes.chipContainer}>
        <SmartTooltipChip
          style={{ backgroundColor: getChipColor(Number(data?.quality)) }}
          label={`${data?.name} - ${data?.quality}`}
          tooltip={data?.description || data?.toolTip}
        />
      </Box>
    );
  };

  // Mobile-specific components
  const TitleAndBar = () => (
    <Box className={classes.titleBarContainer}>
      <Typography className={classes.title}>{title}</Typography>
      <Box className={classes.dietBar}>
        <DietQualityBar
          currentScore={data?.quality || null}
          size="small"
        />
      </Box>
    </Box>
  );

  const ChipAndImage = () => (
    <Box
      className={classes.imageContainer}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        margin: '0 auto',
      }}
    >
      {chipPosition === 'top' && <ChipComponent />}
      {data?.fingerprintPhotoUrl && (
        <Box sx={{ width: '100%', mb: chipPosition === 'bottom' ? 2 : 0 }}>
          <DietPatternImage
            type={type}
            quality={quality}
            isMobile={isMobile}
            style={{ width: '100%', height: 'auto' }}
          />
        </Box>
      )}
      {chipPosition === 'bottom' && <ChipComponent />}
    </Box>
  );

  const ContentSections = () => (
    <Box className={classes.nestedGrid}>
      {/* Nested Column 1 - Can include */}
      <Box className={classes.nestedColumn}>
        <Box className={classes.contentSection}>
          <Typography
            variant="body1"
            className={classes.subTitle}
          >
            Can include:
          </Typography>
          <Typography variant="body2">{data?.description || '--'}</Typography>
        </Box>
      </Box>

      {/* Nested Column 2 - Limits and Does Not Include */}
      <Box className={classes.nestedColumn}>
        <Box className={`${classes.contentSection} ${classes.sectionMargin}`}>
          <Typography
            variant="body1"
            className={classes.subTitle}
          >
            Limits:
          </Typography>
          <Typography variant="body2">{data?.limits || '--'}</Typography>
        </Box>

        <Box className={classes.contentSection}>
          <Typography
            variant="body1"
            className={classes.subTitle}
          >
            Does Not Include:
          </Typography>
          <Typography variant="body2">{data?.lacks || '--'}</Typography>
        </Box>
      </Box>
    </Box>
  );

  // If variant is 'image-only', just return the ChipAndImage component
  if (variant === 'image-only') {
    return <ChipAndImage />;
  }

  // Mobile layout
  if (isMobile) {
    return (
      <>
        {title && (
          <Box className={classes.frame}>
            <Box className={classes.mobileLayout}>
              {/* Title and bar */}
              <TitleAndBar />

              {/* Chip and image under title/bar */}
              <ChipAndImage />

              {/* Content sections at the bottom */}
              <Box sx={{ mt: '24px' }}>
                <ContentSections />
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  }

  // Desktop layout
  return (
    <>
      {title && (
        <Box className={classes.frame}>
          {/* Main 2-column layout using Flexbox instead of Grid for more control */}
          <Box className={classes.mainContainer}>
            {/* Left column - Title, bar, and content */}
            <Box className={classes.contentColumn}>
              {/* Title and bar */}
              <TitleAndBar />

              {/* Nested 2-column layout using Flexbox */}
              <ContentSections />
            </Box>

            {/* Right column - Chip and Image */}
            <Box className={classes.imageColumn}>
              <ChipAndImage />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default DietBox;
